import type {ReadonlyJSONObject} from 'assistant-stream/utils'
import type {ThreadAssistantMessagePart} from '@assistant-ui/react'
import type {Message, TimelineEntry} from './hooks/useChatMessageFormatting'
import {partitionTimelineIntoOrderedSections} from './timelinePartition'
import {buildWorkingSegments} from './buildWorkingSegments'
import {formatAssistantDisplayMarkdown, formatAgentXmlToMarkdown} from './formatAgentXmlToMarkdown'
export type OmicsAssistantContentPart = ThreadAssistantMessagePart

function lookupToolInvocation(
  entries: TimelineEntry[],
  toolCallId: string,
): {
  call: Extract<TimelineEntry, {kind: 'tool_call'}>
  result?: Extract<TimelineEntry, {kind: 'tool_result'}>
} | null {
  let call: Extract<TimelineEntry, {kind: 'tool_call'}> | undefined
  let result: Extract<TimelineEntry, {kind: 'tool_result'}> | undefined
  for (const e of entries) {
    if (e.kind === 'tool_call' && e.tool_call_id === toolCallId) {
      call = e
    }
    if (e.kind === 'tool_result' && e.tool_call_id === toolCallId) {
      result = e
    }
  }
  if (!call) return null
  return {call, result}
}

function toolPartFromCall(
  call: Extract<TimelineEntry, {kind: 'tool_call'}>,
  result?: Extract<TimelineEntry, {kind: 'tool_result'}>,
): OmicsAssistantContentPart {
  const args = (call.tool_input ?? {}) as ReadonlyJSONObject
  const argsText = Object.keys(args).length === 0 ? '' : JSON.stringify(args)
  const base: OmicsAssistantContentPart = {
    type: 'tool-call',
    toolCallId: call.tool_call_id,
    toolName: call.tool_name,
    args,
    argsText,
  }
  if (!result) return base
  return {
    ...base,
    result: result.content,
    isError: !result.ok,
  }
}

function pushText(parts: OmicsAssistantContentPart[], raw: string, answerFormat: Message['answerFormat']): void {
  const t = formatAssistantDisplayMarkdown(raw, answerFormat)
  if (!t.trim()) return
  parts.push({type: 'text', text: t})
}

function pushReasoning(parts: OmicsAssistantContentPart[], raw: string, answerFormat: Message['answerFormat']): void {
  const t = formatAssistantDisplayMarkdown(raw, answerFormat)
  if (!t.trim()) return
  parts.push({type: 'reasoning', text: t})
}

function partsFromV2Timeline(msg: Message): OmicsAssistantContentPart[] {
  const entries = msg.timeline?.entries ?? []
  const parts: OmicsAssistantContentPart[] = []
  const sections = partitionTimelineIntoOrderedSections(entries)

  for (const sec of sections) {
    if (sec.kind === 'preamble') {
      pushText(parts, sec.content, msg.answerFormat)
      continue
    }
    const workingSegments = buildWorkingSegments(sec.entries)
    for (const seg of workingSegments) {
      if (seg.type === 'text') {
        pushReasoning(parts, seg.content, msg.answerFormat)
      } else {
        parts.push(toolPartFromCall(seg.call, seg.result))
      }
    }
  }

  pushText(parts, msg.content ?? '', msg.answerFormat)
  return parts
}

function partsFromStreamingV2(msg: Message): OmicsAssistantContentPart[] {
  const parts: OmicsAssistantContentPart[] = []
  const entries = msg.timeline?.entries ?? []

  const lead = msg.liveLeadMarkdown?.trim() ?? ''
  if (lead) {
    const t = formatAgentXmlToMarkdown(lead)
    if (t.trim()) parts.push({type: 'text', text: t})
  }

  const queue = msg.liveWorkingQueue ?? []
  for (const item of queue) {
    if (item.kind === 'reasoning') {
      const t = formatAgentXmlToMarkdown(item.markdown)
      if (t.trim()) parts.push({type: 'reasoning', text: t})
    } else {
      const pair = lookupToolInvocation(entries, item.toolCallId)
      if (pair) {
        parts.push(toolPartFromCall(pair.call, pair.result))
      } else {
        parts.push({
          type: 'tool-call',
          toolCallId: item.toolCallId,
          toolName: 'pending',
          args: {} as ReadonlyJSONObject,
          argsText: '',
        })
      }
    }
  }

  const stream = msg.content?.trim() ?? ''
  if (stream) {
    const t = formatAgentXmlToMarkdown(stream)
    if (t.trim()) parts.push({type: 'text', text: t})
  }

  return parts
}

/**
 * Maps an Omics `Message` to assistant-ui `ThreadMessageLike.content` parts for the sidebar.
 * Preamble segments become `text` (visible above Working). Working text → `reasoning`; tools → `tool-call`.
 */
export function omicsMessageToAssistantParts(msg: Message): OmicsAssistantContentPart[] {
  if (msg.type === 'user') {
    return []
  }

  if (msg.timeline?.schema_version === 2) {
    if (msg.isStreamingTurn) {
      return partsFromStreamingV2(msg)
    }
    return partsFromV2Timeline(msg)
  }

  const parts: OmicsAssistantContentPart[] = []
  const body = formatAssistantDisplayMarkdown(msg.content ?? '', msg.answerFormat)
  if (body.trim()) {
    parts.push({type: 'text', text: body})
  }
  return parts
}

export function sanitizeAssistantParts(parts: OmicsAssistantContentPart[]): OmicsAssistantContentPart[] {
  const filtered = parts.filter(Boolean)
  if (filtered.length > 0) return filtered
  return [{type: 'text', text: '\u00a0'}]
}
